import { useEffect, useMemo, useRef, useState } from "react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
import { toast } from "sonner";
import { apiFetch } from "../../utils/api";
import { SocialDataSkeleton } from "./SocialSkeleton";
import { Users as UsersIcon, Facebook, Loader2, BarChart3, FileDown, Check, ChevronsUpDown, Sparkles } from "lucide-react";
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis, Tooltip as RTooltip, Cell } from "recharts";
import { PLATFORM_BY_KEY, formatNumber } from "./common";
import { SocialReportPDF, downloadElementAsPDF } from "../SocialPdfReport";

const RankingList = ({ title, color, icon, items, renderValue, emptyMsg, testid }) => (
  <div className={`rounded-lg border ${color.border} ${color.bg} p-3`} data-testid={`ranking-${testid}`}>
    <div className={`flex items-center gap-1.5 mb-3 ${color.text}`}>
      {icon}
      <span className="font-semibold text-sm">{title}</span>
    </div>
    {items.length === 0 ? (
      <div className="text-xs text-slate-500 py-4 text-center">{emptyMsg}</div>
    ) : (
      <ol className="space-y-2">
        {items.map((c, idx) => (
          <li key={c.metricool_brand_id || c.client_id} className="flex items-center gap-2 text-xs">
            <span className={`w-5 h-5 rounded-full flex items-center justify-center font-bold text-[10px] ${idx === 0 ? "bg-yellow-400 text-yellow-900" : idx === 1 ? "bg-slate-300 text-slate-700" : idx === 2 ? "bg-orange-300 text-orange-900" : "bg-slate-100 text-slate-500"}`}>{idx + 1}</span>
            {c.picture && <img src={c.picture} alt="" className="w-5 h-5 rounded-full object-cover" />}
            <span className="flex-1 truncate text-slate-700 font-medium">{c.name}</span>
            <span className="font-bold text-slate-900 whitespace-nowrap">{renderValue(c)}</span>
          </li>
        ))}
      </ol>
    )}
  </div>
);

export function StatsTab() {
  const [stats, setStats] = useState(null);
  const [weeklyStats, setWeeklyStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingWeekly, setLoadingWeekly] = useState(false);
  const [filter, setFilter] = useState("all");
  const [period, setPeriod] = useState(30);
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [brandList, setBrandList] = useState([]);
  const [rankingPeriod, setRankingPeriod] = useState("week");
  const [metricFilter, setMetricFilter] = useState("all");
  const [pdfTarget, setPdfTarget] = useState(null);
  const pdfRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const handleExportPDF = async (clientData, period) => {
    setExporting(true);
    setPdfTarget({ client: clientData, period });
    await new Promise(r => setTimeout(r, 700));
    try {
      if (pdfRef.current) {
        const safeName = (clientData.name || "report").replace(/[^a-z0-9_-]/gi, "_");
        const periodSuffix = period === 7 ? "7gg" : "30gg";
        const dateStr = new Date().toISOString().slice(0, 10);
        await downloadElementAsPDF(pdfRef.current, `Report_${safeName}_${periodSuffix}_${dateStr}.pdf`);
        toast.success("Report PDF generato!");
      }
    } catch (e) {
      toast.error("Errore generazione PDF: " + (e.message || ""));
    } finally {
      setExporting(false);
      setPdfTarget(null);
    }
  };

  const fetchBrands = async () => {
    try {
      const r = await apiFetch(`/social/brands`);
      if (r.ok) {
        const data = await r.json();
        setBrandList(data.map(b => ({ brand_id: b.id, name: b.label, picture: b.picture })));
      }
    } catch { /* ignore */ }
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const qs = filter === "all" ? `?days=${period}` : `?brand_id=${filter}&days=${period}`;
      const r = await apiFetch(`/social-stats${qs}`);
      if (r.ok) setStats(await r.json());
    } finally { setLoading(false); }
  };

  const fetchWeekly = async () => {
    if (filter !== "all") { setWeeklyStats(null); return; }
    setLoadingWeekly(true);
    try {
      const r = await apiFetch(`/social-stats?days=7`);
      if (r.ok) setWeeklyStats(await r.json());
    } finally { setLoadingWeekly(false); }
  };

  useEffect(() => { fetchBrands(); }, []);
  useEffect(() => { fetchStats(); }, [filter, period]); // eslint-disable-line
  useEffect(() => { fetchWeekly(); }, [filter]); // eslint-disable-line

  const platformChartData = useMemo(() => {
    if (!stats) return [];
    return Object.entries(stats.totals.by_platform || {}).map(([k, v]) => ({
      name: PLATFORM_BY_KEY[k]?.label || k,
      key: k,
      followers: v.followers,
      views: v.views || 0,
      accounts: v.accounts,
      color: PLATFORM_BY_KEY[k]?.color || "#94a3b8",
    }));
  }, [stats]);

  const isSingleBrand = filter !== "all";
  const selectedBrand = isSingleBrand ? (stats?.clients?.[0]) : null;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 flex-wrap">
        <Popover open={clientPickerOpen} onOpenChange={setClientPickerOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={clientPickerOpen}
              className="w-[260px] justify-between"
              data-testid="stats-client-filter"
            >
              <span className="truncate">
                {filter === "all"
                  ? `Tutti i clienti (${brandList.length})`
                  : (brandList.find(b => String(b.brand_id) === filter)?.name || "Seleziona cliente")}
              </span>
              <ChevronsUpDown className="w-4 h-4 opacity-50 flex-shrink-0 ml-2" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[260px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Cerca cliente..." data-testid="stats-client-search" />
              <CommandList>
                <CommandEmpty>Nessun cliente trovato.</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    value="all"
                    onSelect={() => { setFilter("all"); setClientPickerOpen(false); }}
                    data-testid="stats-client-option-all"
                  >
                    <Check className={`mr-2 h-4 w-4 ${filter === "all" ? "opacity-100" : "opacity-0"}`} />
                    Tutti i clienti ({brandList.length})
                  </CommandItem>
                  {brandList.map(b => (
                    <CommandItem
                      key={b.brand_id}
                      value={`${b.name} ${b.brand_id}`}
                      onSelect={() => { setFilter(String(b.brand_id)); setClientPickerOpen(false); }}
                      data-testid={`stats-client-option-${b.brand_id}`}
                    >
                      <Check className={`mr-2 h-4 w-4 ${filter === String(b.brand_id) ? "opacity-100" : "opacity-0"}`} />
                      <span className="truncate">{b.name}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <Select value={String(period)} onValueChange={(v) => setPeriod(Number(v))}>
          <SelectTrigger className="w-44" data-testid="stats-period-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Ultimi 7 giorni</SelectItem>
            <SelectItem value="30">Ultimi 30 giorni</SelectItem>
            <SelectItem value="90">Ultimi 90 giorni</SelectItem>
            <SelectItem value="180">Ultimi 180 giorni</SelectItem>
            <SelectItem value="365">Ultimi 365 giorni</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs text-muted-foreground">Dati aggregati da <strong className="text-slate-700">Metricool</strong> · ultimi {period} giorni</div>
      </div>

      {loading ? (
        <SocialDataSkeleton rows={5} />
      ) : !stats ? (
        <Card className="p-12 text-center text-muted-foreground">Nessun dato</Card>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="p-4 bg-gradient-to-br from-slate-900 to-slate-700 text-white" data-testid="kpi-followers">
              <div className="text-xs uppercase tracking-wider text-slate-300 font-medium">Follower totali</div>
              <div className="text-3xl font-bold mt-2">{formatNumber(stats.totals.followers)}</div>
              <div className="text-[11px] text-slate-300 mt-1">su {stats.totals.accounts} account</div>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-fuchsia-500 to-pink-400 text-white" data-testid="kpi-views">
              <div className="text-xs uppercase tracking-wider text-white/80 font-medium">Views totali (30gg)</div>
              <div className="text-3xl font-bold mt-2">{formatNumber(stats.totals.views)}</div>
              <div className="text-[11px] text-white/80 mt-1">reels, post, video</div>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-blue-600 to-blue-400 text-white" data-testid="kpi-engagement">
              <div className="text-xs uppercase tracking-wider text-white/80 font-medium">Engagement medio</div>
              <div className="text-3xl font-bold mt-2">{(stats.totals.engagement || 0).toFixed(2)}%</div>
              <div className="text-[11px] text-white/80 mt-1">{formatNumber(stats.totals.interactions)} interazioni</div>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-amber-500 to-orange-400 text-white" data-testid="kpi-media">
              <div className="text-xs uppercase tracking-wider text-white/80 font-medium">Contenuti pubblicati</div>
              <div className="text-3xl font-bold mt-2">{formatNumber(stats.totals.media_count)}</div>
              <div className="text-[11px] text-white/80 mt-1">{Object.keys(stats.totals.by_platform || {}).length} piattaforme</div>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-5">
              <h3 className="font-semibold mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-slate-600" /> Follower per piattaforma</h3>
              {platformChartData.length === 0 ? (
                <div className="text-sm text-muted-foreground py-6 text-center">Nessuna piattaforma collegata.</div>
              ) : (
                <div className="h-56">
                  <ResponsiveContainer>
                    <BarChart data={platformChartData}>
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={formatNumber} />
                      <RTooltip formatter={(v) => formatNumber(v)} />
                      <Bar dataKey="followers" radius={[8, 8, 0, 0]}>
                        {platformChartData.map((d, i) => <Cell key={i} fill={d.color} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              <div className="grid grid-cols-4 gap-1.5 mt-3">
                {platformChartData.map(d => (
                  <div key={d.key} className="text-center px-2 py-1.5 rounded-md" style={{ background: `${d.color}15` }} data-testid={`platform-follower-${d.key}`}>
                    <div className="text-[10px] uppercase font-semibold" style={{ color: d.color }}>{d.name}</div>
                    <div className="text-sm font-bold text-slate-900">{formatNumber(d.followers)}</div>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-5" data-testid="views-per-platform-card">
              <h3 className="font-semibold mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-fuchsia-600" /> Visualizzazioni per piattaforma</h3>
              {platformChartData.length === 0 || platformChartData.every(d => (d.views || 0) === 0) ? (
                <div className="text-sm text-muted-foreground py-6 text-center">Nessuna view nel periodo.</div>
              ) : (
                <div className="h-56">
                  <ResponsiveContainer>
                    <BarChart data={platformChartData}>
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={formatNumber} />
                      <RTooltip formatter={(v) => formatNumber(v)} />
                      <Bar dataKey="views" radius={[8, 8, 0, 0]}>
                        {platformChartData.map((d, i) => <Cell key={i} fill={d.color} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              <div className="grid grid-cols-4 gap-1.5 mt-3">
                {platformChartData.map(d => (
                  <div key={d.key} className="text-center px-2 py-1.5 rounded-md" style={{ background: `${d.color}15` }} data-testid={`platform-views-${d.key}`}>
                    <div className="text-[10px] uppercase font-semibold" style={{ color: d.color }}>{d.name}</div>
                    <div className="text-sm font-bold text-slate-900">{formatNumber(d.views || 0)}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {isSingleBrand && selectedBrand && (
            <Card className="p-5" data-testid="brand-platform-detail">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h3 className="font-semibold flex items-center gap-2">
                  {selectedBrand.picture && <img src={selectedBrand.picture} alt="" className="w-7 h-7 rounded-full object-cover" />}
                  Statistiche per piattaforma · <span className="text-slate-700">{selectedBrand.name}</span>
                </h3>
                <Button
                  size="sm"
                  className="h-8 gap-1.5 bg-slate-900 hover:bg-slate-800 text-white"
                  disabled={exporting}
                  onClick={() => handleExportPDF(selectedBrand, 30)}
                  data-testid="export-pdf-single-brand"
                >
                  {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
                  Esporta Report PDF
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {selectedBrand.accounts.map(a => {
                  const meta = PLATFORM_BY_KEY[a.platform];
                  const Icon = meta?.icon;
                  return (
                    <div key={a.account_id} className={`rounded-lg border ${meta?.border || 'border-slate-200'} p-4 ${meta?.bg || 'bg-white'}`} data-testid={`platform-card-${a.platform}`}>
                      <div className={`flex items-center gap-2 ${meta?.textHex || 'text-slate-700'} mb-3`}>
                        {Icon ? <Icon className="w-4 h-4" /> : <span className="text-[10px] font-bold w-4 text-center">TT</span>}
                        <span className="font-semibold text-sm">{meta?.label || a.platform}</span>
                        {a.username && <span className="text-[11px] opacity-70 truncate">@{a.username}</span>}
                      </div>
                      <div className="space-y-2">
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500">Follower</div>
                          <div className="text-xl font-bold text-slate-900">{a.followers_count > 0 ? formatNumber(a.followers_count) : <span className="text-slate-400 text-sm font-normal">n/d</span>}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500">Views</div>
                            <div className="text-sm font-semibold text-slate-700">{formatNumber(a.views || 0)}</div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500">Engagement</div>
                            <div className="text-sm font-semibold text-slate-700">{(a.engagement || 0).toFixed(2)}%</div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500">Interazioni</div>
                            <div className="text-sm font-semibold text-slate-700">{formatNumber(a.interactions || 0)}</div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500">Contenuti</div>
                            <div className="text-sm font-semibold text-slate-700">{a.media_count || 0}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {selectedBrand.accounts.length === 0 && (
                  <div className="col-span-full text-sm text-muted-foreground py-6 text-center">Nessuna piattaforma collegata su Metricool per questa brand.</div>
                )}
              </div>
            </Card>
          )}

          {filter === "all" && (
            <Card className="p-5" data-testid="rankings-card">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h3 className="font-semibold flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-slate-600" />
                  Classifiche clienti
                </h3>
                <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5" data-testid="ranking-period-toggle">
                  <button
                    onClick={() => setRankingPeriod("week")}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${rankingPeriod === "week" ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                    data-testid="ranking-period-week"
                  >Settimanale (7gg)</button>
                  <button
                    onClick={() => setRankingPeriod("month")}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${rankingPeriod === "month" ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                    data-testid="ranking-period-month"
                  >Mensile (30gg)</button>
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-4" data-testid="ranking-metric-filter">
                {[
                  { key: "all", label: "Tutte le classifiche", color: "bg-slate-900 text-white" },
                  { key: "totale", label: "Top Totale", color: "bg-violet-600 text-white" },
                  { key: "followers", label: "Top Follower", color: "bg-emerald-600 text-white" },
                  { key: "views", label: "Top Views", color: "bg-fuchsia-600 text-white" },
                  { key: "engagement", label: "Top Engagement", color: "bg-blue-600 text-white" },
                  { key: "ads", label: "Top Lead (Meta Ads)", color: "bg-amber-600 text-white" },
                ].map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setMetricFilter(opt.key)}
                    data-testid={`metric-chip-${opt.key}`}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition border ${metricFilter === opt.key ? `${opt.color} border-transparent shadow-sm` : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
                  >{opt.label}</button>
                ))}
              </div>

              {(() => {
                const source = rankingPeriod === "week" ? weeklyStats : stats;
                if (rankingPeriod === "week" && loadingWeekly && !weeklyStats) {
                  return <div className="text-sm text-muted-foreground py-8 text-center">Caricamento classifiche settimanali...</div>;
                }
                if (!source || !source.clients) {
                  return <div className="text-sm text-muted-foreground py-8 text-center">Dati non disponibili.</div>;
                }
                const allBrands = source.clients;
                const topN = metricFilter === "all" ? 5 : 10;

                const topFollowers = [...allBrands].filter(c => (c.followers_growth || 0) !== 0).sort((a, b) => (b.followers_growth || 0) - (a.followers_growth || 0)).slice(0, topN);
                const topViews = [...allBrands].filter(c => (c.views || 0) > 0).sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, topN);
                const topEng = [...allBrands].filter(c => (c.interactions || 0) > 0).sort((a, b) => (b.interactions || 0) - (a.interactions || 0)).slice(0, topN);
                const adsBrands = allBrands.filter(c => c.meta_ads && (c.meta_ads.leads > 0 || c.meta_ads.spend > 0));
                const topLeads = [...adsBrands].sort((a, b) => {
                  const cplA = a.meta_ads.cpl || 999999;
                  const cplB = b.meta_ads.cpl || 999999;
                  if ((b.meta_ads.leads || 0) !== (a.meta_ads.leads || 0)) return (b.meta_ads.leads || 0) - (a.meta_ads.leads || 0);
                  return cplA - cplB;
                }).slice(0, topN);

                const maxFg = Math.max(1, ...allBrands.map(c => c.followers_growth || 0));
                const maxV = Math.max(1, ...allBrands.map(c => c.views || 0));
                const maxI = Math.max(1, ...allBrands.map(c => c.interactions || 0));
                const maxL = Math.max(1, ...allBrands.map(c => c.meta_ads?.leads || 0));
                const totaleScored = allBrands.map(c => {
                  const fg = Math.max(0, c.followers_growth || 0) / maxFg;
                  const v = (c.views || 0) / maxV;
                  const i = (c.interactions || 0) / maxI;
                  const l = (c.meta_ads?.leads || 0) / maxL;
                  const score = (fg * 0.25 + v * 0.30 + i * 0.30 + l * 0.15) * 100;
                  return { ...c, _score: score };
                }).filter(c => c._score > 0).sort((a, b) => b._score - a._score).slice(0, topN);

                const cards = {
                  followers: { title: "Top Follower acquisiti", color: { border: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-700" }, icon: <UsersIcon className="w-4 h-4" />, items: topFollowers, renderValue: (c) => <span className={c.followers_growth > 0 ? "text-emerald-600" : "text-red-600"}>{c.followers_growth > 0 ? "+" : ""}{formatNumber(c.followers_growth)}</span>, emptyMsg: "Nessuna variazione follower nel periodo" },
                  views: { title: "Top Visualizzazioni", color: { border: "border-fuchsia-200", bg: "bg-fuchsia-50", text: "text-fuchsia-700" }, icon: <BarChart3 className="w-4 h-4" />, items: topViews, renderValue: (c) => formatNumber(c.views), emptyMsg: "Nessuna view nel periodo" },
                  engagement: { title: "Top Engagement (interazioni)", color: { border: "border-blue-200", bg: "bg-blue-50", text: "text-blue-700" }, icon: <Sparkles className="w-4 h-4" />, items: topEng, renderValue: (c) => <span>{formatNumber(c.interactions)} <span className="text-[10px] opacity-60">({c.engagement.toFixed(1)}%)</span></span>, emptyMsg: "Nessuna interazione nel periodo" },
                  ads: { title: "Top Meta Ads (Lead · CPL)", color: { border: "border-amber-200", bg: "bg-amber-50", text: "text-amber-700" }, icon: <Facebook className="w-4 h-4" />, items: topLeads, renderValue: (c) => (<span className="text-right"><span className="text-amber-700 font-bold">{c.meta_ads.leads} lead</span><span className="block text-[10px] opacity-70">€{c.meta_ads.cpl.toFixed(2)} CPL · €{c.meta_ads.spend.toFixed(0)}</span></span>), emptyMsg: `Nessun brand con Meta Ads attivi. ${source.totals.meta_ads.brands_count > 0 ? `(${source.totals.meta_ads.brands_count} brand collegato, ma 0 spesa nel periodo)` : "Collega FB Ads in Metricool."}` },
                  totale: { title: "Top clienti totale", color: { border: "border-violet-200", bg: "bg-violet-50", text: "text-violet-700" }, icon: <Sparkles className="w-4 h-4" />, items: totaleScored, renderValue: (c) => (<span className="text-right"><span className="text-violet-700 font-bold">{c._score.toFixed(1)} pt</span><span className="block text-[10px] opacity-70">+{formatNumber(c.followers_growth || 0)} · {formatNumber(c.views || 0)} v · {formatNumber(c.interactions || 0)} int</span></span>), emptyMsg: "Nessun dato sufficiente per il ranking" },
                };

                if (metricFilter === "all") {
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <RankingList testid="followers" {...cards.followers} />
                      <RankingList testid="views" {...cards.views} />
                      <RankingList testid="engagement" {...cards.engagement} />
                      <RankingList testid="ads" {...cards.ads} />
                    </div>
                  );
                }
                const c = cards[metricFilter];
                return (
                  <div className="space-y-3">
                    <RankingList testid={metricFilter} {...c} />
                  </div>
                );
              })()}

              {((rankingPeriod === "week" ? weeklyStats : stats)?.totals?.meta_ads?.spend || 0) > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">Totali Meta Ads ({rankingPeriod === "week" ? "7gg" : "30gg"})</div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    {(() => {
                      const ads = (rankingPeriod === "week" ? weeklyStats : stats).totals.meta_ads;
                      return (
                        <>
                          <div className="bg-emerald-50 border border-emerald-200 rounded-md p-2">
                            <div className="text-[10px] text-emerald-700">Lead totali</div>
                            <div className="text-lg font-bold text-emerald-900">{ads.leads}</div>
                          </div>
                          <div className="bg-slate-50 border border-slate-200 rounded-md p-2">
                            <div className="text-[10px] text-slate-700">Spesa</div>
                            <div className="text-lg font-bold text-slate-900">€{ads.spend.toFixed(2)}</div>
                          </div>
                          <div className="bg-blue-50 border border-blue-200 rounded-md p-2">
                            <div className="text-[10px] text-blue-700">CPC medio</div>
                            <div className="text-lg font-bold text-blue-900">€{(ads.cpc_avg || 0).toFixed(2)}</div>
                          </div>
                          <div className="bg-fuchsia-50 border border-fuchsia-200 rounded-md p-2">
                            <div className="text-[10px] text-fuchsia-700">CPM medio</div>
                            <div className="text-lg font-bold text-fuchsia-900">€{(ads.cpm_avg || 0).toFixed(2)}</div>
                          </div>
                          <div className="bg-amber-50 border border-amber-200 rounded-md p-2">
                            <div className="text-[10px] text-amber-700">CPL medio</div>
                            <div className="text-lg font-bold text-amber-900">€{(ads.cpl_avg || 0).toFixed(2)}</div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </Card>
          )}

          <Card className="p-5">
            <h3 className="font-semibold mb-3">Dettaglio per cliente</h3>
            <div className="space-y-3">
              {stats.clients?.map(c => (
                <div key={c.client_id} className="border rounded-lg p-3" data-testid={`stats-client-${c.client_id}`}>
                  <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
                    <div className="font-medium flex items-center gap-2">
                      {c.picture && <img src={c.picture} alt="" className="w-7 h-7 rounded-full object-cover border border-slate-200" />}
                      {c.name}
                    </div>
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 items-center">
                      <span><strong>{formatNumber(c.followers)}</strong> follower</span>
                      {c.views > 0 && <span><strong>{formatNumber(c.views)}</strong> views</span>}
                      {c.engagement > 0 && <span><strong>{c.engagement.toFixed(2)}%</strong> engagement</span>}
                      <span><strong>{c.media_count}</strong> contenuti</span>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-[11px] gap-1 border-slate-300 hover:bg-slate-900 hover:text-white hover:border-slate-900"
                        disabled={exporting}
                        onClick={() => handleExportPDF(c, 30)}
                        data-testid={`export-pdf-${c.client_id}`}
                      >
                        {exporting && pdfTarget?.client?.metricool_brand_id === c.metricool_brand_id
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <FileDown className="w-3 h-3" />}
                        Esporta PDF
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {c.accounts.length === 0 && <span className="text-xs text-muted-foreground">Nessun account collegato</span>}
                    {c.accounts.map(a => {
                      const meta = PLATFORM_BY_KEY[a.platform];
                      return (
                        <div key={a.account_id} className={`text-xs px-2 py-1 rounded-md flex items-center gap-1.5 ${meta?.bg} ${meta?.textHex} border ${meta?.border}`}>
                          {meta?.icon ? <meta.icon className="w-3 h-3" /> : <span className="text-[9px] font-bold">TT</span>}
                          {a.username ? `@${a.username}` : meta?.label} · {a.followers_count > 0 ? formatNumber(a.followers_count) : "n/d"} follower
                        </div>
                      );
                    })}
                  </div>

                  {c.accounts.some(a => (a.views || 0) > 0) && (
                    <div className="mt-2.5 pt-2.5 border-t border-dashed border-slate-200">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Visualizzazioni per piattaforma</div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {c.accounts.map(a => {
                          const meta = PLATFORM_BY_KEY[a.platform];
                          const Icon = meta?.icon;
                          if ((a.views || 0) === 0 && (a.engagement || 0) === 0) return null;
                          return (
                            <div key={`v_${a.account_id}`} className={`rounded-md border ${meta?.border} ${meta?.bg} px-2 py-1.5`} data-testid={`views-${c.client_id}-${a.platform}`}>
                              <div className={`flex items-center gap-1 text-[10px] ${meta?.textHex} font-medium`}>
                                {Icon ? <Icon className="w-2.5 h-2.5" /> : <span className="text-[8px] font-bold">TT</span>}
                                {meta?.label || a.platform}
                              </div>
                              <div className="font-bold text-slate-900 text-sm leading-tight">{formatNumber(a.views || 0)}</div>
                              <div className="text-[9px] text-slate-500">eng {(a.engagement || 0).toFixed(1)}% · {formatNumber(a.interactions || 0)} int</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </>
      )}

      {pdfTarget?.client && (
        <div style={{ position: "fixed", left: "-10000px", top: 0, zIndex: -1, pointerEvents: "none" }} aria-hidden="true">
          <SocialReportPDF ref={pdfRef} client={pdfTarget.client} period={pdfTarget.period} />
        </div>
      )}
    </div>
  );
}
